import React, { useState } from 'react';
import { ArrowLeft, Lock, Eye, EyeOff, ArrowRight, CheckCircle } from 'lucide-react';

interface ResetPasswordPageProps {
    token: string;
    onSubmit: (token: string, newPassword: string) => Promise<string>;
    onBack: () => void;
}

const colors = {
    background: '#0F1A24',
    backgroundLight: '#162434',
    backgroundCard: '#1C2E42',
    backgroundWell: '#0A1218',
    textPrimary: '#E6EBF0',
    textSecondary: '#A9B4C2',
    textMuted: '#7F8C9C',
    accent: '#3A6A7A',
    accentLight: '#4A8A9A',
    accentDark: '#2E4A5A',
    border: '#1C2E42',
    success: '#10B981',
    error: '#EF4444',
};

export const ResetPasswordPage: React.FC<ResetPasswordPageProps> = ({
    token,
    onSubmit,
    onBack,
}) => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (password.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setIsLoading(true);
        try {
            await onSubmit(token, password);
            setSuccess(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to reset password');
        } finally {
            setIsLoading(false);
        }
    };

    if (success) {
        return (
            <div
                className="min-h-screen flex items-center justify-center"
                style={{
                    background: colors.background,
                    fontFamily:
                        "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        backgroundImage: 'url(/assets/realsignal-bg-texture.jpg)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        opacity: 0.12,
                        mixBlendMode: 'luminosity',
                        filter: 'grayscale(100%)',
                        pointerEvents: 'none',
                    }}
                />
                <div className="relative z-10 w-full max-w-md mx-auto px-6">
                    <div
                        style={{
                            background: `linear-gradient(145deg, ${colors.backgroundCard} 0%, ${colors.backgroundLight} 100%)`,
                            borderRadius: '1.5rem',
                            padding: '2rem',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                            textAlign: 'center',
                        }}
                    >
                        <div
                            style={{
                                width: '64px',
                                height: '64px',
                                borderRadius: '50%',
                                background: `rgba(16, 185, 129, 0.1)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 1.5rem',
                            }}
                        >
                            <CheckCircle
                                style={{ width: '32px', height: '32px', color: colors.success }}
                            />
                        </div>
                        <h1
                            style={{
                                fontSize: '1.5rem',
                                fontWeight: 700,
                                color: colors.textPrimary,
                                marginBottom: '0.75rem',
                            }}
                        >
                            Password Reset Complete
                        </h1>
                        <p
                            style={{
                                color: colors.textSecondary,
                                marginBottom: '1.5rem',
                                lineHeight: 1.5,
                            }}
                        >
                            Your password has been successfully reset. You can now sign in with your
                            new password.
                        </p>
                        <button
                            onClick={onBack}
                            style={{
                                width: '100%',
                                background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentDark} 100%)`,
                                color: 'white',
                                fontWeight: 600,
                                padding: '1rem 1.5rem',
                                borderRadius: '1.5rem',
                                border: `2px solid ${colors.accent}`,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem',
                            }}
                        >
                            <span>Sign In</span>
                            <ArrowRight className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className="min-h-screen flex items-center justify-center"
            style={{
                background: colors.background,
                fontFamily:
                    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: 'url(/assets/realsignal-bg-texture.jpg)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    opacity: 0.12,
                    mixBlendMode: 'luminosity',
                    filter: 'grayscale(100%)',
                    pointerEvents: 'none',
                }}
            />
            <div className="relative z-10 w-full max-w-md mx-auto px-6">
                <div
                    style={{
                        background: `linear-gradient(145deg, ${colors.backgroundCard} 0%, ${colors.backgroundLight} 100%)`,
                        borderRadius: '1.5rem',
                        padding: '2rem',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    }}
                >
                    <button
                        onClick={onBack}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            color: colors.textSecondary,
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            marginBottom: '1.5rem',
                            padding: 0,
                        }}
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span>Back to Sign In</span>
                    </button>

                    <h1
                        style={{
                            fontSize: '1.5rem',
                            fontWeight: 700,
                            color: colors.textPrimary,
                            marginBottom: '0.5rem',
                        }}
                    >
                        Create New Password
                    </h1>
                    <p
                        style={{
                            color: colors.textSecondary,
                            marginBottom: '1.5rem',
                            lineHeight: 1.5,
                        }}
                    >
                        Enter your new password below. Make sure it's at least 8 characters.
                    </p>

                    <form onSubmit={handleSubmit}>
                        <div style={{ marginBottom: '1rem' }}>
                            <label
                                style={{
                                    display: 'block',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: colors.textSecondary,
                                    marginBottom: '0.5rem',
                                }}
                            >
                                New Password
                            </label>
                            <div className="relative">
                                <Lock
                                    style={{
                                        position: 'absolute',
                                        left: '0.75rem',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: colors.textMuted,
                                        width: '1.25rem',
                                        height: '1.25rem',
                                        zIndex: 10,
                                    }}
                                />
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    placeholder="Enter new password"
                                    style={{
                                        width: '100%',
                                        background: colors.backgroundWell,
                                        border: `1px solid ${colors.border}`,
                                        borderRadius: '0.75rem',
                                        padding: '0.75rem 3rem 0.75rem 2.75rem',
                                        color: colors.textPrimary,
                                        fontSize: '0.875rem',
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={{
                                        position: 'absolute',
                                        right: '0.75rem',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: colors.textMuted,
                                        background: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                        zIndex: 10,
                                    }}
                                >
                                    {showPassword ? (
                                        <EyeOff className="w-5 h-5" />
                                    ) : (
                                        <Eye className="w-5 h-5" />
                                    )}
                                </button>
                            </div>
                        </div>

                        <div style={{ marginBottom: '1.5rem' }}>
                            <label
                                style={{
                                    display: 'block',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: colors.textSecondary,
                                    marginBottom: '0.5rem',
                                }}
                            >
                                Confirm Password
                            </label>
                            <div className="relative">
                                <Lock
                                    style={{
                                        position: 'absolute',
                                        left: '0.75rem',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: colors.textMuted,
                                        width: '1.25rem',
                                        height: '1.25rem',
                                        zIndex: 10,
                                    }}
                                />
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={e => setConfirmPassword(e.target.value)}
                                    placeholder="Confirm new password"
                                    style={{
                                        width: '100%',
                                        background: colors.backgroundWell,
                                        border: `1px solid ${colors.border}`,
                                        borderRadius: '0.75rem',
                                        padding: '0.75rem 3rem 0.75rem 2.75rem',
                                        color: colors.textPrimary,
                                        fontSize: '0.875rem',
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    style={{
                                        position: 'absolute',
                                        right: '0.75rem',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: colors.textMuted,
                                        background: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                        zIndex: 10,
                                    }}
                                >
                                    {showConfirmPassword ? (
                                        <EyeOff className="w-5 h-5" />
                                    ) : (
                                        <Eye className="w-5 h-5" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {error && (
                            <div
                                style={{
                                    background: 'rgba(239, 68, 68, 0.1)',
                                    border: '1px solid rgba(239, 68, 68, 0.3)',
                                    borderRadius: '0.5rem',
                                    padding: '0.75rem 1rem',
                                    color: '#FCA5A5',
                                    fontSize: '0.875rem',
                                    marginBottom: '1rem',
                                }}
                            >
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={isLoading}
                            style={{
                                width: '100%',
                                background: isLoading
                                    ? colors.accentDark
                                    : `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentDark} 100%)`,
                                color: 'white',
                                fontWeight: 600,
                                padding: '1rem 1.5rem',
                                borderRadius: '1.5rem',
                                border: `2px solid ${colors.accent}`,
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem',
                                opacity: isLoading ? 0.7 : 1,
                            }}
                        >
                            <span>{isLoading ? 'Resetting...' : 'Reset Password'}</span>
                            {!isLoading && <ArrowRight className="w-5 h-5" />}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordPage;
